import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

/**
 * TypingIndicator - Beautiful typing indicator with animations
 * 
 * Shows who is typing with animated dots and avatars
 * Supports multiple users typing simultaneously
 */
const TypingIndicator = ({ typingUsers, currentUser }) => {
  const intl = useIntl();
  if (!typingUsers || typingUsers.length === 0) return null;

  // Filter out current user from typing indicators
  const otherUsersTyping = typingUsers.filter(user => user.id !== currentUser?.id);

  if (otherUsersTyping.length === 0) return null;

  const getTypingText = (users) => {
    if (users.length === 1) {
      return intl.formatMessage({ id: 'succession.messaging.typing_indicator.one_typing' }, { name: users[0].allowed_name || users[0].name });
    } else if (users.length === 2) {
      return intl.formatMessage({ id: 'succession.messaging.typing_indicator.two_typing' }, { name1: users[0].allowed_name || users[0].name, name2: users[1].allowed_name || users[1].name });
    } else if (users.length === 3) {
      return intl.formatMessage({ id: 'succession.messaging.typing_indicator.three_typing' }, { name1: users[0].allowed_name || users[0].name, name2: users[1].allowed_name || users[1].name, name3: users[2].allowed_name || users[2].name });
    } else {
      return intl.formatMessage({ id: 'succession.messaging.typing_indicator.many_typing' }, { name: users[0].allowed_name || users[0].name, count: users.length - 1 });
    }
  };

  return (
    <div style={{
      padding: '12px 20px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      backgroundColor: 'var(--succession-bg-secondary)', // Changed from #f8fafc
      borderTop: '1px solid var(--succession-border)', // Changed from #f1f5f9
      animation: 'slideUp 0.3s ease-out'
    }}>
      {/* CSS Animations */}
      <style>
        {`
          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes bounce {
            0%, 60%, 100% {
              transform: translateY(0);
            }
            30% {
              transform: translateY(-8px);
            }
          }
          
          @keyframes pulse {
            0%, 100% {
              opacity: 0.4;
            }
            50% {
              opacity: 1;
            }
          }
          
          .typing-dot {
            width: 6px;
            height: 6px;
            background-color: var(--succession-text-muted);
            border-radius: 50%;
            animation: bounce 1.4s infinite;
          }
          
          .typing-dot:nth-child(1) {
            animation-delay: 0s;
          }
          
          .typing-dot:nth-child(2) {
            animation-delay: 0.2s;
          }
          
          .typing-dot:nth-child(3) {
            animation-delay: 0.4s;
          }
          
          .typing-avatar {
            animation: pulse 2s infinite;
          }
        `}
      </style>

      {/* User Avatars */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
      }}>
        {otherUsersTyping.slice(0, 3).map((user, index) => (
          <div
            key={user.id}
            className="typing-avatar"
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              backgroundColor: 'var(--succession-primary)', // Changed from #6366f1
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--succession-bg-primary)', // Changed from white
              fontSize: '10px',
              fontWeight: '600',
              border: '2px solid var(--succession-bg-primary)', // Changed from #ffffff
              marginLeft: index > 0 ? '-8px' : '0',
              zIndex: 10 - index,
              position: 'relative'
            }}
            title={user.allowed_name || user.name}
          >
            {(user.allowed_name || user.name || '?').charAt(0).toUpperCase()}
          </div>
        ))}
        
        {otherUsersTyping.length > 3 && (
          <div style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: 'var(--succession-text-muted)', // Changed from #9ca3af
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--succession-bg-primary)', // Changed from white
            fontSize: '9px',
            fontWeight: '600',
            border: '2px solid var(--succession-bg-primary)', // Changed from #ffffff
            marginLeft: '-8px'
          }}>
            +{otherUsersTyping.length - 3}
          </div>
        )}
      </div>

      {/* Typing Text and Dots */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flex: 1
      }}>
        <span style={{
          fontSize: '13px',
          color: 'var(--succession-text-muted)', // Changed from #6b7280
          fontStyle: 'italic'
        }}>
          {getTypingText(otherUsersTyping)}
        </span>
        
        {/* Animated Dots */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '3px'
        }}>
          <div className="typing-dot"></div>
          <div className="typing-dot"></div>
          <div className="typing-dot"></div>
        </div>
      </div>
    </div>
  );
};

TypingIndicator.propTypes = {
  typingUsers: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    allowed_name: PropTypes.string
  })),
  currentUser: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    allowed_name: PropTypes.string
  })
};

export default TypingIndicator; 